<!--
  Venda parcelada (chamado 2026-0067) — o formulário da venda inteira.

  Em cima, o resumo (forma, parcelas, total pago, taxas, líquido, quantas já caíram). Depois o que
  é da VENDA e vale para todas as parcelas (forma, data em que o cliente pagou, observações). Por
  fim, uma linha por parcela com o que é DELA: quando cai na conta e a taxa do adquirente. O valor
  de uma parcela não se edita — a soma das parcelas é a venda.
-->
<template>
  <div class="recebimento-parcelado-form">
    <box type="primary" :title="t('Resumo da venda')" elem-id="boxResumoVendaParcelada">
      <div class="resumo" data-testid="venda-parcelada-resumo">
        <div class="resumo-item">
          <span class="resumo-label">{{ t('Forma de pagamento') }}</span>
          <strong data-testid="venda-parcelada-resumo-forma">{{ nomeDaForma }}</strong>
        </div>
        <div class="resumo-item">
          <span class="resumo-label">{{ t('Parcelas') }}</span>
          <strong data-testid="venda-parcelada-resumo-parcelas">{{ tp('{n}x', { n: parcelas.length }) }}</strong>
        </div>
        <div class="resumo-item">
          <span class="resumo-label">{{ t('Total pago') }}</span>
          <strong data-testid="venda-parcelada-resumo-total">{{ n(totais.valor / 100, 'currency') }}</strong>
        </div>
        <div class="resumo-item">
          <span class="resumo-label">{{ t('Taxas') }}</span>
          <strong data-testid="venda-parcelada-resumo-taxas">{{ n(totais.taxas / 100, 'currency') }}</strong>
        </div>
        <div class="resumo-item">
          <span class="resumo-label">{{ t('Líquido') }}</span>
          <strong data-testid="venda-parcelada-resumo-liquido">{{ n(totais.liquido / 100, 'currency') }}</strong>
        </div>
        <div class="resumo-item">
          <span class="resumo-label">{{ t('Disponíveis') }}</span>
          <strong data-testid="venda-parcelada-resumo-disponiveis">{{ tp('{d} de {n}', { d: disponiveis, n: parcelas.length }) }}</strong>
        </div>
      </div>
    </box>

    <box type="primary" :title="t('Venda')" elem-id="boxVendaParcelada">
      <p class="field-hint">{{ t('Vale para todas as parcelas desta venda.') }}</p>
      <buttons-list
        data-testid="venda-parcelada-forma"
        :placeholder="t('Escolha a forma de pagamento')"
        :validation="() => !!venda.recebimento_forma_pagamento_id"
        :required="true"
        :disabled="readOnly"
        elem-id="venda_parcelada_forma_pagamento_id"
        v-model="venda.recebimento_forma_pagamento_id"
        :itens-select="formasQueAceitam">
      </buttons-list>
      <div class="form-row">
        <div class="form-col">
          <input-date
            elem-id="venda_parcelada_data"
            data-testid="venda-parcelada-data"
            :label="t('Data do pagamento')"
            :validation="() => !!venda.recebimento_data"
            :required="true"
            :disabled="readOnly"
            v-model="venda.recebimento_data">
          </input-date>
        </div>
      </div>
      <input-rich-text
        data-testid="venda-parcelada-observacoes"
        :placeholder="t('Observações')"
        :required="false"
        :disabled="readOnly"
        elem-id="venda_parcelada_observacoes"
        v-model="venda.recebimento_observacoes">
      </input-rich-text>
    </box>

    <box type="primary" :title="t('Parcelas')" elem-id="boxParcelasVendaParcelada">
      <p class="field-hint">
        {{ t('Cada parcela tem a sua taxa. O valor e a data em que a parcela fica disponível não mudam — para mudar a venda, exclua-a e registre de novo.') }}
      </p>
      <div class="parcelas" data-testid="venda-parcelada-parcelas">
        <div class="parcela parcela-cabecalho">
          <span>#</span>
          <span>{{ t('Valor') }}</span>
          <span>{{ t('Disponível em') }}</span>
          <span>{{ t('Taxa') }}</span>
          <span>{{ t('Líquido') }}</span>
        </div>
        <div
          v-for="(parcela, i) in parcelas"
          :key="parcela.recebimento_id"
          class="parcela"
          :data-testid="`venda-parcelada-parcela-${parcela.recebimento_parcela_numero}`">
          <span class="parcela-numero">
            {{ parcela.recebimento_parcela_numero }}/{{ parcelas.length }}
            <span
              class="parcela-status"
              :class="jaCaiu(parcela) ? 'status-disponivel' : 'status-aguardando'"
              :data-testid="`venda-parcelada-parcela-${parcela.recebimento_parcela_numero}-status`">
              {{ jaCaiu(parcela) ? t('disponível') : t('aguardando') }}
            </span>
          </span>
          <span :data-testid="`venda-parcelada-parcela-${parcela.recebimento_parcela_numero}-valor`">{{ n(parcela.recebimento_valor / 100, 'currency') }}</span>
          <!-- Só leitura: quando a parcela cai na conta vem do prazo da forma (ou do provedor). -->
          <span :data-testid="`venda-parcelada-parcela-${parcela.recebimento_parcela_numero}-disponivel`">{{ dataNaTela(parcela.recebimento_data_disponivel) }}</span>
          <input-currency
            :elem-id="`venda_parcelada_parcela_${i}_taxa`"
            :data-testid="`venda-parcelada-parcela-${parcela.recebimento_parcela_numero}-taxa`"
            :validation="() => parcela.recebimento_valor_taxas >= 0 && parcela.recebimento_valor_taxas <= parcela.recebimento_valor"
            :required="false"
            :disabled="readOnly"
            :model-value="parcela.recebimento_valor_taxas / 100"
            @update:model-value="(v: number) => { parcela.recebimento_valor_taxas = Math.round((Number(v) || 0) * 100) }">
          </input-currency>
          <span :data-testid="`venda-parcelada-parcela-${parcela.recebimento_parcela_numero}-liquido`">{{ n((parcela.recebimento_valor - parcela.recebimento_valor_taxas) / 100, 'currency') }}</span>
        </div>
      </div>
    </box>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { tComParametros } from '@/utils/tComParametros'
import { dataIso, hojeLocal, parcelasMaxDaForma, totaisDaVenda, type ParcelaEditavel } from '@/composables/useParcelamento'
import type { FormasPagamento_FormaPagamentoRow } from '@/contracts/FormasPagamento_FormaPagamentoRow'
import type { VendaEditavel } from '@/composables/useVendaParcelada'
import { useDateFormatter } from '@/composables/useDateFormatter'

const props = withDefaults(defineProps<{
  venda: VendaEditavel
  parcelas: ParcelaEditavel[]
  formas: FormasPagamento_FormaPagamentoRow[]
  readOnly?: boolean
}>(), {
  readOnly: false,
})

const { t, n } = useI18n()
const tp = (chave: string, parametros: Record<string, string | number>): string => tComParametros(t, chave, parametros)

const { parseBackendDate, formatDate } = useDateFormatter()
/** YYYY-MM-DD → a data no formato do idioma da pessoa (typed data layer). */
const dataNaTela = (iso: string): string => formatDate(parseBackendDate(dataIso(iso))) || '-'

const hoje = hojeLocal()
const jaCaiu = (p: ParcelaEditavel): boolean => { const d = dataIso(p.recebimento_data_disponivel); return d !== '' && d <= hoje }

const totais = computed(() => totaisDaVenda(props.parcelas))
const disponiveis = computed(() => props.parcelas.filter(jaCaiu).length)

/** Só as formas que aceitam este número de parcelas — trocar para outra seria recusado. A atual fica sempre. */
const formasQueAceitam = computed(() => props.formas
  .filter((f) => parcelasMaxDaForma(f) >= props.parcelas.length || Number(f.forma_pagamento_id) === Number(props.venda.recebimento_forma_pagamento_id))
  .map((f) => ({ ID: Number(f.forma_pagamento_id), Name: f.forma_pagamento_nome })))

const nomeDaForma = computed(() =>
  props.formas.find((f) => Number(f.forma_pagamento_id) === Number(props.venda.recebimento_forma_pagamento_id))?.forma_pagamento_nome ?? '-'
)

/** O que impede de salvar, em português (como no RecebimentoForm, chamado 2026-0033). */
const pendencias = computed<string[]>(() => {
  const faltando: string[] = []
  if (!props.venda.recebimento_forma_pagamento_id) faltando.push(t('Escolha a forma de pagamento'))
  if (!props.venda.recebimento_data) faltando.push(t('Informe a data do pagamento'))
  for (const p of props.parcelas) {
    if (p.recebimento_valor_taxas < 0 || p.recebimento_valor_taxas > p.recebimento_valor) {
      faltando.push(tp('A taxa da parcela {i} não pode passar do valor dela', { i: p.recebimento_parcela_numero }))
    }
  }
  return faltando
})

defineExpose({ pendencias })
</script>

<style scoped>
.resumo {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
}

.resumo-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.resumo-label,
.field-hint {
  color: #6c757d;
  font-size: 0.8rem;
}

.field-hint {
  margin: 0 0 10px;
}

.form-row {
  display: flex;
  gap: 20px;
  margin: 12px 0;
}

.form-col {
  flex: 1 1 calc(50% - 10px);
  min-width: 220px;
}

.parcelas {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.parcela {
  display: grid;
  grid-template-columns: 110px 1fr 1.4fr 1.2fr 1fr;
  gap: 10px;
  align-items: center;
}

.parcela-cabecalho {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
}

.parcela-numero {
  display: flex;
  flex-direction: column;
  font-weight: 600;
}

.parcela-status {
  font-size: 0.7rem;
  font-weight: 500;
}

.status-disponivel {
  color: var(--p-green-600, #16a34a);
}

.status-aguardando {
  color: #6c757d;
}

@media (max-width: 768px) {
  .parcela {
    grid-template-columns: 1fr 1fr;
  }

  .parcela-cabecalho {
    display: none;
  }
}
</style>
